import { useState, useEffect } from 'react';
import { format, differenceInDays } from 'date-fns';
import {
  ShieldCheck,
  AlertTriangle,
  Clock,
  Plus,
  Search,
  Eye,
  Trash2,
  FileCheck,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  certificatesApi,
  Certificate,
  MaterialExpiry,
  CertificatesDashboard,
  CertificateType,
} from '@/api/services/certificates.api';

const certTypeLabels: Record<CertificateType, string> = {
  MTR: 'Mill Test Report',
  COC: 'Certificate of Conformance',
  COA: 'Certificate of Analysis',
  CALIBRATION: 'Calibration',
  SAFETY: 'Safety',
  ENVIRONMENTAL: 'Environmental',
  QUALITY: 'Quality',
  OTHER: 'Other',
};

const statusColors: Record<string, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  EXPIRED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  REVOKED: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  PENDING_RENEWAL: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

const expiryStatusColors: Record<string, string> = {
  VALID: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  EXPIRING_SOON: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  EXPIRED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  DISPOSED: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
};

export default function Compliance() {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [materialExpiries, setMaterialExpiries] = useState<MaterialExpiry[]>([]);
  const [dashboard, setDashboard] = useState<CertificatesDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCert, setSelectedCert] = useState<Certificate | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Form state
  const [formData, setFormData] = useState({
    type: 'MTR' as CertificateType,
    name: '',
    issuedBy: '',
    issuedDate: '',
    expiryDate: '',
    documentUrl: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [certsRes, expiryRes, dashboardRes] = await Promise.all([
        certificatesApi.getAll({ limit: 50 }),
        certificatesApi.getMaterialExpiries({ limit: 50 }),
        certificatesApi.getDashboard(),
      ]);
      setCertificates(certsRes.data);
      setMaterialExpiries(expiryRes.data);
      setDashboard(dashboardRes);
    } catch (error) {
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCertificate = async () => {
    if (!formData.name || !formData.issuedBy || !formData.issuedDate) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      await certificatesApi.create({
        ...formData,
        expiryDate: formData.expiryDate || undefined,
        documentUrl: formData.documentUrl || undefined,
        notes: formData.notes || undefined,
      });
      toast.success('Certificate created');
      setCreateModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Failed to create certificate');
    }
  };

  const handleViewCertificate = async (cert: Certificate) => {
    try {
      const detailed = await certificatesApi.getById(cert.id);
      setSelectedCert(detailed);
      setDetailSheetOpen(true);
    } catch (error) {
      toast.error('Failed to load certificate details');
    }
  };

  const handleDeleteCertificate = async (id: string) => {
    try {
      await certificatesApi.delete(id);
      toast.success('Certificate deleted');
      setDetailSheetOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to delete certificate');
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'MTR',
      name: '',
      issuedBy: '',
      issuedDate: '',
      expiryDate: '',
      documentUrl: '',
      notes: '',
    });
  };

  const filteredCertificates = certificates.filter((cert) => {
    const matchesSearch =
      cert.certificateNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.issuedBy.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || cert.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || cert.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getDaysUntilExpiry = (expiryDate?: string) => {
    if (!expiryDate) return null;
    const days = differenceInDays(new Date(expiryDate), new Date());
    return days;
  };

  const getExpiryBadge = (expiryDate?: string) => {
    if (!expiryDate) return null;
    const days = getDaysUntilExpiry(expiryDate);
    if (days === null) return null;

    if (days < 0) {
      return <Badge className={statusColors.EXPIRED}>Expired</Badge>;
    } else if (days <= 30) {
      return (
        <Badge className={statusColors.PENDING_RENEWAL}>
          {days} days left
        </Badge>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Compliance & Certificates</h1>
          <p className="text-muted-foreground">Manage certificates and material expiry tracking</p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Certificate
        </Button>
      </div>

      {/* KPI Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Active Certificates</p>
                  <p className="text-3xl font-semibold">{dashboard.certificates.active}</p>
                </div>
                <ShieldCheck className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Expiring (30 days)</p>
                  <p className="text-3xl font-semibold">{dashboard.certificates.expiring}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Expired Certs</p>
                  <p className="text-3xl font-semibold">{dashboard.certificates.expired}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Material Expiry Alerts</p>
                  <p className="text-3xl font-semibold">
                    {dashboard.materialExpiry.expiring + dashboard.materialExpiry.expired}
                  </p>
                </div>
                <Package className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="certificates" className="space-y-4">
        <TabsList>
          <TabsTrigger value="certificates">Certificates</TabsTrigger>
          <TabsTrigger value="material-expiry">Material Expiry</TabsTrigger>
        </TabsList>

        <TabsContent value="certificates" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search certificates..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(certTypeLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
                <SelectItem value="REVOKED">Revoked</SelectItem>
                <SelectItem value="PENDING_RENEWAL">Pending Renewal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Certificates Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Certificate #</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Issued By</TableHead>
                  <TableHead>Issued Date</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCertificates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground">
                      No certificates found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCertificates.map((cert) => (
                    <TableRow key={cert.id}>
                      <TableCell className="font-medium">{cert.certificateNumber}</TableCell>
                      <TableCell>
                        <div>
                          <p>{cert.name}</p>
                          {cert._count?.items ? (
                            <p className="text-xs text-muted-foreground">
                              {cert._count.items} linked items
                            </p>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{certTypeLabels[cert.type]}</Badge>
                      </TableCell>
                      <TableCell>{cert.issuedBy}</TableCell>
                      <TableCell>{format(new Date(cert.issuedDate), 'MMM d, yyyy')}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {cert.expiryDate
                            ? format(new Date(cert.expiryDate), 'MMM d, yyyy')
                            : 'No expiry'}
                          {getExpiryBadge(cert.expiryDate)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[cert.status]}>
                          {cert.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewCertificate(cert)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="material-expiry" className="space-y-4">
          {/* Material Expiry Table */}
          <Card>
            <CardHeader>
              <CardTitle>Material Expiry Tracking</CardTitle>
              <CardDescription>Track materials approaching or past expiry dates</CardDescription>
            </CardHeader>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Batch #</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Expiry Date</TableHead>
                  <TableHead>Days Left</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materialExpiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      No material expiry records
                    </TableCell>
                  </TableRow>
                ) : (
                  materialExpiries.map((exp) => (
                    <TableRow key={exp.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{exp.item.name}</p>
                          <p className="text-xs text-muted-foreground">{exp.item.sku}</p>
                        </div>
                      </TableCell>
                      <TableCell>{exp.batchNumber || '-'}</TableCell>
                      <TableCell>{exp.location.name}</TableCell>
                      <TableCell>{exp.quantity}</TableCell>
                      <TableCell>{format(new Date(exp.expiryDate), 'MMM d, yyyy')}</TableCell>
                      <TableCell>
                        {exp.daysUntilExpiry !== undefined ? (
                          <span
                            className={
                              exp.daysUntilExpiry < 0
                                ? 'text-red-600 font-medium'
                                : exp.daysUntilExpiry <= 30
                                ? 'text-amber-600 font-medium'
                                : ''
                            }
                          >
                            {exp.daysUntilExpiry < 0
                              ? `${Math.abs(exp.daysUntilExpiry)} days overdue`
                              : `${exp.daysUntilExpiry} days`}
                          </span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={expiryStatusColors[exp.status]}>
                          {exp.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Certificate Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Certificate</DialogTitle>
            <DialogDescription>Add a new certificate or compliance document</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, type: value as CertificateType }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(certTypeLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Certificate name"
              />
            </div>
            <div className="space-y-2">
              <Label>Issued By *</Label>
              <Input
                value={formData.issuedBy}
                onChange={(e) => setFormData((prev) => ({ ...prev, issuedBy: e.target.value }))}
                placeholder="Issuing organization"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issued Date *</Label>
                <Input
                  type="date"
                  value={formData.issuedDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, issuedDate: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, expiryDate: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Document URL</Label>
              <Input
                value={formData.documentUrl}
                onChange={(e) => setFormData((prev) => ({ ...prev, documentUrl: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCertificate}>Create Certificate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certificate Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          {selectedCert && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <SheetTitle>{selectedCert.certificateNumber}</SheetTitle>
                  <Badge className={statusColors[selectedCert.status]}>
                    {selectedCert.status.replace('_', ' ')}
                  </Badge>
                </div>
                <SheetDescription>{selectedCert.name}</SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Certificate Info */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Type:</span>
                        <p className="font-medium">{certTypeLabels[selectedCert.type]}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Issued By:</span>
                        <p className="font-medium">{selectedCert.issuedBy}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Issued Date:</span>
                        <p className="font-medium">
                          {format(new Date(selectedCert.issuedDate), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Expiry Date:</span>
                        <p className="font-medium">
                          {selectedCert.expiryDate
                            ? format(new Date(selectedCert.expiryDate), 'MMM d, yyyy')
                            : 'No expiry'}
                        </p>
                      </div>
                      {selectedCert.documentUrl && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Document:</span>
                          <p>
                            <a
                              href={selectedCert.documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              View Document
                            </a>
                          </p>
                        </div>
                      )}
                      {selectedCert.notes && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Notes:</span>
                          <p>{selectedCert.notes}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Linked Items */}
                {selectedCert.items && selectedCert.items.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Linked Items</CardTitle>
                    </CardHeader>
                    <div className="px-6 pb-6 space-y-2">
                      {selectedCert.items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between border rounded-lg p-3"
                        >
                          <div className="flex items-center gap-3">
                            {item.item ? (
                              <>
                                <Package className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{item.item.name}</p>
                                  <p className="text-xs text-muted-foreground">{item.item.sku}</p>
                                </div>
                              </>
                            ) : item.equipment ? (
                              <>
                                <FileCheck className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{item.equipment.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {item.equipment.assetNumber}
                                  </p>
                                </div>
                              </>
                            ) : null}
                          </div>
                          {item.batchNumber && (
                            <Badge variant="outline">Batch: {item.batchNumber}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    onClick={() => handleDeleteCertificate(selectedCert.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
